import React, { useEffect, useState, useMemo } from 'react';
import Map, { Source, Layer } from 'react-map-gl/maplibre';
import type { LayerProps } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { StyleSpecification } from 'maplibre-gl';

interface MalawiDistrictsMapProps {
  incidentData?: Record<string, number>;
  incidentsData?: any;
  loading?: boolean;
  height?: number;
  onCategoryDataChange?: (categoryData: any) => void; // Callback to pass category data to parent
  onLocationClick?: (locationName: string, locationType: 'district' | 'constituency') => void; // New callback for location clicks
}

// Map types for switching between different GeoJSON sources
type MapType = 'districts' | 'constituencies';

// 🎨 Dynamic incident-based color scale algorithm (matching MalawiMapOnly)
const calculateDynamicRanges = (incidentCounts: number[]) => {
  const nonZeroCounts = incidentCounts.filter((count: number) => count > 0).sort((a: number, b: number) => a - b);

  if (nonZeroCounts.length === 0) {
    return {
      veryLow: { min: 1, max: 2 },
      low: { min: 3, max: 4 },
      medium: { min: 5, max: 9 },
      high: { min: 10, max: 19 },
      critical: { min: 20, max: Infinity }
    };
  }

  const min = nonZeroCounts[0];
  const max = nonZeroCounts[nonZeroCounts.length - 1];
  const range = max - min;

  // Calculate quintiles (5 equal groups)
  const quintileSize = range / 5;

  return {
    veryLow: {
      min: min,
      max: Math.ceil(min + quintileSize)
    },
    low: {
      min: Math.ceil(min + quintileSize) + 1,
      max: Math.ceil(min + quintileSize * 2)
    },
    medium: {
      min: Math.ceil(min + quintileSize * 2) + 1,
      max: Math.ceil(min + quintileSize * 3)
    },
    high: {
      min: Math.ceil(min + quintileSize * 3) + 1,
      max: Math.ceil(min + quintileSize * 4)
    },
    critical: {
      min: Math.ceil(min + quintileSize * 4) + 1,
      max: max
    }
  };
};

// Dynamic color function
const getColor = (count: number, ranges: any): string => {
  if (count === 0) return '#f3f4f6';     // No Incidents - Gray-100
  if (count >= ranges.veryLow.min && count <= ranges.veryLow.max) return '#dbeafe';   // Very Low - Blue-100
  if (count >= ranges.low.min && count <= ranges.low.max) return '#bfdbfe';   // Low - Blue-200
  if (count >= ranges.medium.min && count <= ranges.medium.max) return '#fbbf24';   // Medium - Amber-400
  if (count >= ranges.high.min && count <= ranges.high.max) return '#f59e0b'; // High - Amber-500
  return '#dc2626'; // Critical - Red-600
};

// Get color category for styling and legend
const getColorCategory = (count: number, ranges: any): string => {
  if (count === 0) return 'none';
  if (count >= ranges.veryLow.min && count <= ranges.veryLow.max) return 'very-low';
  if (count >= ranges.low.min && count <= ranges.low.max) return 'low';
  if (count >= ranges.medium.min && count <= ranges.medium.max) return 'medium';
  if (count >= ranges.high.min && count <= ranges.high.max) return 'high';
  return 'critical';
};

// Get category label for display
const getCategoryLabel = (count: number, ranges: any): string => {
  if (count === 0) return 'No Incidents';
  if (count >= ranges.veryLow.min && count <= ranges.veryLow.max) return `Very Low (${ranges.veryLow.min}-${ranges.veryLow.max})`;
  if (count >= ranges.low.min && count <= ranges.low.max) return `Low (${ranges.low.min}-${ranges.low.max})`;
  if (count >= ranges.medium.min && count <= ranges.medium.max) return `Medium (${ranges.medium.min}-${ranges.medium.max})`;
  if (count >= ranges.high.min && count <= ranges.high.max) return `High (${ranges.high.min}-${ranges.high.max})`;
  return `Critical (${ranges.critical.min}+)`;
};

// Normalize keys: collapse spaces, trim, lowercase
const norm = (s?: string | null) =>
  s?.toString().replace(/\s+/g, ' ').trim().toLowerCase() ?? '';

// Enhanced name matching for better data mapping
const findBestMatch = (targetName: string, dataMap: Record<string, number>): number => {
  const normalizedTarget = norm(targetName);

  // Direct match first
  if (dataMap[normalizedTarget] !== undefined) {
    return dataMap[normalizedTarget];
  }

  // Try exact match in original data
  const exactMatch = Object.keys(dataMap).find(key => norm(key) === normalizedTarget);
  if (exactMatch) {
    return dataMap[exactMatch];
  }

  // Try partial matches for common variations
  const partialMatch = Object.keys(dataMap).find(key => {
    const normalizedKey = norm(key);
    return normalizedKey.includes(normalizedTarget) || normalizedTarget.includes(normalizedKey);
  });

  if (partialMatch) {
    console.log(`📊 Found partial match: "${targetName}" -> "${partialMatch}"`);
    return dataMap[partialMatch];
  }

  return 0;
};

const MalawiDistrictsMap: React.FC<MalawiDistrictsMapProps> = ({
  incidentsData,
  loading: externalLoading = false,
  height = 750,
  onCategoryDataChange,
  onLocationClick
}) => {
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapType, setMapType] = useState<MapType>('districts');
  const [hovered, setHovered] = useState<{ name: string; count: number; x: number; y: number; category: string } | null>(null);
  const [dynamicRanges, setDynamicRanges] = useState<any>(null);

  // Debug props received
  console.log('🚀 MalawiDistrictsMap props received:');
  console.log('  - incidentsData:', incidentsData);
  console.log('  - incidentsData type:', typeof incidentsData);
  console.log('  - incidentsData keys:', incidentsData ? Object.keys(incidentsData) : 'null/undefined');
  console.log('  - externalLoading:', externalLoading);
  console.log('  - Current mapType:', mapType);

  // Debug the specific data we'll use
  if (incidentsData) {
    console.log('  - grouped_by_district:', incidentsData.grouped_by_district);
    console.log('  - grouped_by_constituency:', incidentsData.grouped_by_constituency);
    console.log('  - category_counts:', incidentsData.category_counts);
  }

  // **EMERGENCY DEBUG** - Check if this component is even re-rendering when data changes
  console.log('🔄 Component render timestamp:', new Date().toISOString());

  // ✅ Extract and transform incident data from API response
  const districtIncidentMap = useMemo(() => {
    console.log('📊 Processing incident data for map:', incidentsData);

    if (!incidentsData) {
      console.log('📊 No incidents data available');
      return {};
    }

    // For districts: use grouped_by_district
    // For constituencies: use grouped_by_constituency
    const sourceData = mapType === 'districts'
      ? incidentsData.grouped_by_district
      : incidentsData.grouped_by_constituency;

    if (!sourceData) {
      console.log(`📊 No ${mapType === 'districts' ? 'grouped_by_district' : 'grouped_by_constituency'} data found`);
      return {};
    }

    console.log(`📊 Source data for ${mapType}:`, sourceData);

    // Create a map of normalized name -> incident count
    const map: Record<string, number> = {};

    Object.entries(sourceData).forEach(([locationName, incidentCount]) => {
      if (locationName && typeof incidentCount === 'number') {
        const normalizedName = norm(locationName);
        map[normalizedName] = incidentCount;
        console.log(`📊 Mapped: "${locationName}" -> normalized: "${normalizedName}" -> count: ${incidentCount}`);
      }
    });

    console.log(`📊 Final ${mapType} incident map:`, map);
    return map;
  }, [incidentsData, mapType]);

  // ✅ Load GeoJSON data based on map type
  useEffect(() => {
    const loadGeoJSON = async () => {
      setLoading(true);
      setError(null);
      setHovered(null); // Clear hover state

      try {
        const url = mapType === 'districts'
          ? '/districts_malawi.geojson'
          : '/2025.geojson';

        console.log(`🗺️ Loading GeoJSON from: ${url}`);
        const res = await fetch(url);

        if (!res.ok) {
          throw new Error(`Failed to fetch ${url}: ${res.statusText}`);
        }

        const data = await res.json();

        if (!data?.features) {
          throw new Error('GeoJSON missing features');
        }

        console.log(`✅ Loaded ${data.features.length} features from ${url}`);
        setGeoJsonData(data);

      } catch (err) {
        console.error('❌ Failed to load GeoJSON:', err);
        setError(`Failed to load map data: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    loadGeoJSON();
  }, [mapType]);

  // ✅ Build normalized lookup for incident data
  const normalizedIncidentData = useMemo(() => {
    // Convert the incident map to have normalized keys for faster lookup
    const normalizedMap: Record<string, number> = {};

    Object.entries(districtIncidentMap).forEach(([name, count]) => {
      normalizedMap[norm(name)] = count;
    });

    return normalizedMap;
  }, [districtIncidentMap]);

  // ✅ Enrich features with incidentCount + color
  const enrichedGeoJson = useMemo(() => {
    if (!geoJsonData?.features) return null;

    console.log('📊 Processing GeoJSON features...');
    console.log(`📊 Available incident data for ${mapType}:`, districtIncidentMap);

    // First pass: collect all incident counts to calculate dynamic ranges
    const allCounts = geoJsonData.features.map((f: any) => {
      const geoJsonName = f?.properties?.name;
      return findBestMatch(geoJsonName, districtIncidentMap);
    });

    // Calculate dynamic ranges based on actual data
    const ranges = calculateDynamicRanges(allCounts);
    setDynamicRanges(ranges);

    console.log('📊 Dynamic ranges calculated for incidents dashboard:', ranges);
    console.log('📊 Incident counts distribution:', {
      min: Math.min(...allCounts.filter((c: number) => c > 0)),
      max: Math.max(...allCounts),
      total: allCounts.reduce((sum: number, c: number) => sum + c, 0),
      locations: allCounts.length
    });

    const features = geoJsonData.features.map((f: any) => {
      const geoJsonName = f?.properties?.name;
      const count = findBestMatch(geoJsonName, districtIncidentMap);

      console.log(`📊 GeoJSON feature: "${geoJsonName}" -> incidents: ${count}`);

      return {
        ...f,
        properties: {
          ...f.properties,
          incidentCount: count,
          color: getColor(count, ranges),
        },
      };
    });

    // Dev hint: show matching statistics
    if (process.env.NODE_ENV !== 'production') {
      const withIncidents = features.filter((f: any) => (f.properties?.incidentCount ?? 0) > 0);
      const withoutIncidents = features.filter((f: any) => (f.properties?.incidentCount ?? 0) === 0);

      console.debug(`[Map features] ${mapType} with incidents: ${withIncidents.length}`);
      console.debug(`[Map features] ${mapType} without incidents: ${withoutIncidents.length}`);

      if (withIncidents.length > 0) {
        console.debug(`[Map features] Areas with incidents:`,
          withIncidents.map((f: any) => ({ name: f.properties?.name, count: f.properties?.incidentCount }))
        );
      }
    }

    return { ...geoJsonData, features };
  }, [geoJsonData, districtIncidentMap, mapType]);

  // ✅ Minimal blank basemap (matching MalawiMapOnly)
  const blankStyle = useMemo<StyleSpecification>(
    () => ({
      version: 8,
      name: 'blank',
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources: {},
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: { 'background-color': '#f5f7fb' },
        },
      ],
    }),
    []
  );

  // Enhanced map layers with better styling
  const fillLayerId = 'malawi-fill';

  const fillLayer: LayerProps = {
    id: fillLayerId,
    type: 'fill',
    paint: {
      'fill-color': ['get', 'color'],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        0.95,
        0.8
      ],
    },
  };

  const borderLayer: LayerProps = {
    id: 'malawi-borders',
    type: 'line',
    paint: {
      'line-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#1f2937',
        '#6b7280'
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        2,
        1
      ],
    },
  };

  const labelLayer: LayerProps = {
    id: 'malawi-labels',
    type: 'symbol',
    layout: {
      'text-field': ['coalesce', ['get', 'abbr'], ['get', 'name'], ''],
      'text-size': mapType === 'districts' ? 11 : 8,
      'text-anchor': 'center',
      'symbol-placement': 'point',
    },
    paint: {
      'text-color': '#1f2937',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
    },
  };

  // Extract category data for summary
  const categoryData = incidentsData?.category_counts;

  // Debug category data
  console.log('📊 Category data extracted:', categoryData);
  console.log('📊 Category data type:', typeof categoryData);
  console.log('📊 Category data keys:', categoryData ? Object.keys(categoryData) : 'no keys');

  // Notify parent component about category data changes
  React.useEffect(() => {
    if (onCategoryDataChange && categoryData) {
      onCategoryDataChange(categoryData);
    }
  }, [categoryData, onCategoryDataChange]);

  if (loading) {
    console.log('🔄 Showing loading spinner - internal loading:', loading, 'external loading:', externalLoading);
    return (
      <div
        className="flex items-center justify-center transition-all duration-500 ease-in-out shadow-xl bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl"
        style={{ height: `${height}px` }}
      >
        <div className="text-center animate-fade-in">
          {/* Enhanced loading animation */}
          <div className="relative">
            {/* Outer ring */}
            <div className="w-16 h-16 mx-auto border-4 border-blue-200 rounded-full animate-spin"></div>
            {/* Inner spinning ring */}
            <div className="absolute w-12 h-12 mx-auto border-4 rounded-full top-2 left-2 animate-spin border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
            {/* Center dot */}
            <div className="absolute w-4 h-4 bg-blue-600 rounded-full top-6 left-6 animate-pulse"></div>
          </div>

          {/* Loading text with typing animation */}
          <div className="mt-4">
            <p className="font-medium text-gray-700 animate-pulse">Loading {mapType === 'districts' ? 'Districts' : 'Constituencies'} Map</p>
            <div className="flex justify-center mt-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-48 mx-auto mt-4">
            <div className="h-2 overflow-hidden bg-gray-200 rounded-full">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 animate-pulse" style={{
                width: '100%',
                animation: 'loading-progress 2s ease-in-out infinite'
              }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // **CRITICAL DEBUG** - Check what's preventing map from showing
  console.log('🗺️ Map should render now - Final state check:');
  console.log('  - internal loading:', loading);
  console.log('  - external loading:', externalLoading);
  console.log('  - error:', error);
  console.log('  - geoJsonData exists:', !!geoJsonData);
  console.log('  - enrichedGeoJson exists:', !!enrichedGeoJson);

  if (error) {
    return (
      <div
        className="flex items-center justify-center border-2 border-red-300 border-dashed bg-gradient-to-br from-red-50 to-pink-100 rounded-2xl animate-fade-in"
        style={{ height: `${height}px` }}
      >
        <div className="text-center animate-shake">
          {/* Enhanced error icon */}
          <div className="relative mx-auto mb-4">
            <svg className="w-16 h-16 mx-auto text-red-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            {/* Animated warning pulse */}
            <div className="absolute w-4 h-4 bg-red-500 rounded-full -top-1 -right-1 animate-ping"></div>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-red-900">Map Loading Error</h3>
          <p className="max-w-sm mx-auto text-sm text-red-700">{error}</p>

          {/* Retry suggestion */}
          <div className="p-3 mt-4 bg-white border border-red-200 rounded-lg shadow-sm">
            <p className="text-xs text-gray-600">Try refreshing the page or check your connection</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden shadow-xl bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl" style={{ height }}>
      {/* Map Type Toggle Icon */}
      <div className="absolute z-20 top-4 left-4">
        <button
          onClick={() => setMapType(mapType === 'districts' ? 'constituencies' : 'districts')}
          className="p-3 transition-all duration-200 bg-white border border-gray-200 shadow-xl rounded-xl backdrop-blur-sm bg-opacity-95 hover:bg-gray-50 hover:shadow-2xl hover:scale-105"
          title={`Switch to ${mapType === 'districts' ? 'Constituencies' : 'Districts'} view`}
        >
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600">
              {mapType === 'districts' ? (
                // Districts icon - show constituencies icon to indicate what clicking will do
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ) : (
                // Constituencies icon - show districts icon to indicate what clicking will do  
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-gray-900">
                {mapType === 'districts' ? 'Districts' : 'Constituencies'}
              </div>
              <div className="text-xs text-gray-500">
                Click to switch
              </div>
            </div>
          </div>
        </button>
      </div>



      {/* Enhanced Map Container */}
      <div className="relative w-full h-full">
        <Map
          key={`malawi-map-${mapType}`} // Force re-render when map type changes
          initialViewState={{
            latitude: -13.2543,
            longitude: 34.3015,
            zoom: mapType === 'districts' ? 5.2 : 5.2,
            pitch: 0,
            bearing: 0
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle={blankStyle}
          maxZoom={7.0}
          minZoom={5.2}
          interactiveLayerIds={[fillLayerId]}
          onMouseMove={(e) => {
            const feature: any = e.features?.[0];
            if (feature?.properties && dynamicRanges) {
              const count = feature.properties.incidentCount ?? 0;
              setHovered({
                name: feature.properties.name,
                count: count,
                category: getColorCategory(count, dynamicRanges),
                x: e.point.x,
                y: e.point.y,
              });
            } else {
              setHovered(null);
            }
          }}
          onMouseLeave={() => setHovered(null)}
          onClick={(e) => {
            const feature: any = e.features?.[0];
            if (feature?.properties) {
              const locationName = feature.properties.name;
              const locationType = mapType === 'districts' ? 'district' : 'constituency';

              console.log(`🗺️ Location clicked: ${locationName} (${locationType})`);

              // Call the parent callback if provided
              if (onLocationClick) {
                onLocationClick(locationName, locationType);
              }
            }
          }}
        >
          {enrichedGeoJson?.features?.length > 0 && (
            <Source id="malawi-map-source" type="geojson" data={enrichedGeoJson}>
              <Layer {...fillLayer} />
              <Layer {...borderLayer} />
              <Layer {...labelLayer} />
            </Source>
          )}
        </Map>
      </div>

      {/* 🎨 Enhanced Modern Legend - Compact Version */}
      <div className="absolute z-20 bottom-4 left-4">
        <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-xl backdrop-blur-sm bg-opacity-95">
          {/* Legend Header */}
          <div className="px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600">
            <div className="flex items-center space-x-1.5">
              <div className="p-1 rounded-md bg-white/20 backdrop-blur-sm">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xs font-bold text-white">Incident Density</h3>
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>

          {/* Dynamic Legend Items */}
          <div className="p-2 space-y-1.5">
            {dynamicRanges ? (
              <>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded shadow-sm"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">No Incidents</span>
                  <span className="ml-auto font-mono text-xs text-gray-400">0</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-blue-100 border-blue-200"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Very Low</span>
                  <span className="ml-auto font-mono text-xs text-blue-600">{dynamicRanges.veryLow.min}-{dynamicRanges.veryLow.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-blue-200 border-blue-300"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Low</span>
                  <span className="ml-auto font-mono text-xs text-blue-700">{dynamicRanges.low.min}-{dynamicRanges.low.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-amber-400 border-amber-500"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Medium</span>
                  <span className="ml-auto font-mono text-xs text-amber-700">{dynamicRanges.medium.min}-{dynamicRanges.medium.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-amber-500 border-amber-600"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">High</span>
                  <span className="ml-auto font-mono text-xs text-amber-800">{dynamicRanges.high.min}-{dynamicRanges.high.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 bg-red-600 border border-red-700 rounded shadow-sm"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Critical</span>
                  <span className="ml-auto font-mono text-xs text-red-700">{dynamicRanges.critical.min}+</span>
                </div>
              </>
            ) : (
              <div className="text-xs text-gray-500 text-center py-2">
                Calculating ranges...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 🎯 Enhanced Tooltip */}
      {hovered && (
        <div
          className="absolute z-30 transform -translate-x-1/2 -translate-y-full pointer-events-none"
          style={{
            left: hovered.x,
            top: hovered.y - 12,
          }}
        >
          <div className="relative">
            {/* Tooltip Arrow */}
            <div className="absolute transform -translate-x-1/2 top-full left-1/2">
              <div className="w-0 h-0 border-t-4 border-l-4 border-r-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
            </div>

            {/* Tooltip Content */}
            <div className="px-4 py-3 bg-gray-900 shadow-2xl rounded-xl backdrop-blur-sm min-w-max">
              <div className="mb-1 text-sm font-bold text-white">
                {hovered.name}
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full shadow-sm"
                  style={{ backgroundColor: dynamicRanges ? getColor(hovered.count, dynamicRanges) : '#f3f4f6' }}
                ></div>
                <span className="text-xs text-gray-300">
                  <span className="font-semibold text-white">{hovered.count}</span> incident{hovered.count !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Enhanced Severity Badge with dynamic ranges */}
              {dynamicRanges && (
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    hovered.category === 'critical' ? 'bg-red-600 text-red-100' :
                    hovered.category === 'high' ? 'bg-amber-500 text-amber-100' :
                    hovered.category === 'medium' ? 'bg-amber-400 text-amber-900' :
                    hovered.category === 'low' ? 'bg-blue-200 text-blue-900' :
                    hovered.category === 'very-low' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {getCategoryLabel(hovered.count, dynamicRanges)}
                  </span>
                </div>
              )}

              {/* Severity Badge */}
              {hovered.category !== 'none' && (
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${hovered.category === 'critical' ? 'bg-red-800 text-red-100' :
                    hovered.category === 'high' ? 'bg-red-700 text-red-100' :
                      hovered.category === 'medium' ? 'bg-amber-600 text-amber-100' :
                        hovered.category === 'low' ? 'bg-amber-500 text-amber-100' :
                          'bg-amber-400 text-amber-900'
                    }`}>
                    {hovered.category === 'critical' ? '🚨 Critical' :
                      hovered.category === 'high' ? '⚠️ High Risk' :
                        hovered.category === 'medium' ? '⚡ Medium' :
                          hovered.category === 'low' ? '📊 Low' :
                            '📍 Very Low'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MalawiDistrictsMap;
